import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from "@angular/core";
// Interfaces
import { Client } from "../../../../../interfaces/ClientGlobalInterface.type";
import { Entity } from "../../../../../interfaces/EntityGlobalInterface.type";
import { Agrupation } from "../../../../../interfaces/AgrupationGlobalInterface.type";
import { HighlightTextDirective } from "../../../../../directives/highlight-text-directive/highligth-text.directive";

/**
 * Client > entity > agrupation tree used to switch the current selection, highlighting `filterText` matches.
 * Emits `updateNavigationFlag` with the chosen client, entity and agrupation; `responsive` adapts the layout.
 */
@Component({
  selector: "app-navbar-top-navigation-tree",
  imports: [HighlightTextDirective],
  templateUrl: "./navbar-top-navigation-tree.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./navbar-top-navigation-tree.component.scss"
})
export class NavbarTopNavigationTreeComponent {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  @Input()
  get clientList(): Client[] {
    return this._clientList;
  }
  set clientList(clientList: Client[]) {
    this._clientList = clientList;
    this.getClientHeight();
  }
  _clientList: Client[];
  @Input() filterText: string;
  @Input() responsive: boolean = false;
  @Input()
  get update(): boolean {
    return this._update;
  }
  set update(update: boolean) {
    this._update = update;
    this.getClientHeight();
  }
  _update: boolean;
  @Output() updateNavigationFlag = new EventEmitter<{
    client: Client;
    entity: Entity;
    agrupation: Agrupation;
  }>();
  Math = Math;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor() {}

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  // Go-to-selection flag
  updateNavigation(option?: {
    client: Client;
    entity: Entity;
    agrupation: Agrupation;
    visible: boolean;
  }): void {
    this.updateNavigationFlag.emit(option);
  }

  // Adjust client sizes
  getClientHeight(): void {
    // Agrupation
    const agrupationWidth = 200;
    const agrupationWidthPadding = 20;
    const agrupationHeight = 30;
    const agrupationWithAliasHeight = 46;
    const minAgrupationPerColumn = 3;
    // Entity
    const entityHeightPadding = 30;
    const entityWithAliasHeightPadding = 46;
    const entityMargin = 20;
    // Client
    const clientPadding = 10;
    // Panel
    const panelWidth =
      window.innerWidth -
      28 * Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
    const panelWidthPadding = 140;

    this._clientList.forEach(client => {
      // Visible client
      if (!client["hidden"]) {
        let height = 0;
        let accumulatedWidth = 0;
        let accumulatedHeight = 0;
        let sameRowEntities: Entity[] = [];
        client.entityList.forEach((entity, i) => {
          // Visible entity
          if (!entity["hidden"]) {
            // Visible agrupations
            const visibleAgrupations = entity.agrupations.filter(
              agrupation => !agrupation["hidden"]
            );
            if (this.responsive) {
              // Agrupations with alias
              const agrupationsWithAlias = visibleAgrupations.filter(
                agrupation => agrupation.alias
              )?.length;
              entity["height"] =
                agrupationsWithAlias * agrupationWithAliasHeight +
                (visibleAgrupations.length - agrupationsWithAlias) * agrupationHeight +
                (entity.alias ? entityWithAliasHeightPadding : entityHeightPadding);
              accumulatedHeight += Math.max(42, entity["height"]) + entityMargin;
            } else {
              // Max columns according to panel width
              const columnsAvailable = Math.floor(
                (panelWidth - panelWidthPadding) / agrupationWidth
              );
              // Agrupations per column
              const agrupationsPerColumn = this.Math.max(
                Math.ceil(visibleAgrupations.length / columnsAvailable),
                minAgrupationPerColumn
              );
              // Agrupations with alias
              const agrupationsWithAlias = visibleAgrupations.some(agrupation => agrupation.alias);
              // Entity height
              entity["height"] =
                agrupationsPerColumn *
                  (agrupationsWithAlias ? agrupationWithAliasHeight : agrupationHeight) +
                (entity.alias ? entityWithAliasHeightPadding : entityHeightPadding);
              // Entity columns
              entity["columns"] = this.Math.max(
                Math.ceil(visibleAgrupations.length / agrupationsPerColumn),
                1
              );
              // Entity width
              entity["width"] =
                entity["columns"] * agrupationWidth + agrupationWidthPadding + entityMargin;

              // Check row overflow
              accumulatedWidth += entity["columns"] * agrupationWidth + agrupationWidthPadding;
              if (accumulatedWidth > panelWidth - panelWidthPadding) {
                if (sameRowEntities.length > 0) {
                  height +=
                    Math.max(...sameRowEntities.map(entity => entity["height"])) + entityMargin;
                  accumulatedWidth = entity["columns"] * agrupationWidth + agrupationWidthPadding;
                  sameRowEntities = [entity];
                } else {
                  height += entity["height"] + entityMargin;
                  accumulatedWidth = 0;
                  sameRowEntities = [];
                }
              } else {
                sameRowEntities.push(entity);
              }
            }
          }
          // Final entity check
          if (this.responsive) {
            height = accumulatedHeight;
          } else if (i === client.entityList.length - 1 && sameRowEntities.length > 0) {
            height += Math.max(...sameRowEntities.map(entity => entity["height"])) + entityMargin;
          }
        });
        client["height"] = height + clientPadding;
      }
    });
  }
}
